/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import {useViewSubGroups} from '@ion/applications/src/hooks/useViewSubGroups';
import {
  NAME,
  PREFERENCE,
  setAssetViewSubGroups
} from '@ion/applications/src/pages/admin/assets/state';
import {translate} from '@ion/core/src/locale/locale';
import {AssetsFilters, GenericObject} from '@ion/core/src/types/entities';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {ViewSubGroupsSwitch} from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import Breadcrumb from '../components/breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '../components/breadcrumbs/BreadcrumbUtils';
import {useAnimatedMount} from '../hooks/useAnimatedMount';
import {useExtendStyles} from '../hooks/useExtendStyles';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import useThemeContext from '../theme/useThemeContext';
import {
  DLSTheme,
  PageViewSubGroupsProps,
  ViewSubGroupsPosition
} from '../types/types';
import AssetDetailViewGrid from './grids/AssetDetailViewGrid';
import {AssetAssociation} from './AssetEditor';
import styles, {getClasses} from './editorStyles';

const styleOverrides = (theme: DLSTheme) => ({
  formWrapper : {
    overflowY                      : 'auto',
    [theme.breakpoints.down('sm')] : {
      padding : '0 20px'
    }
  },
  formDialogTitle : {
    [theme.breakpoints.down('sm')] : {
      padding : '16px 8px'
    }
  },
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  closeButton : {
    [theme.breakpoints.down('sm')] : {
      left : '10px'
    }
  },
  paddingNone : {
    display                        : 'flex',
    flexDirection                  : 'column',
    height                         : 'calc(100vh - 332px)',
    padding                        : 0,
    overflowY                      : 'hidden !important',
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 284px)'
    }
  },
  deviceDetailHeader : {
    margin         : '30px auto',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  subAccount : {
    marginRight : 'auto',
    paddingLeft : '35px'
  },
  label : {
    ...dlsTypography('h1'),
    fontSize      : '20px',
    textTransform : toggleThemeStyle('uppercase', 'capitalize')
  },
  chevron : {
    fill : dlsColor('grey3')
  },
  activeCrumb : {
    color         : dlsColor('grey1'),
    textTransform : toggleThemeStyle('none', 'capitalize')
  },
  inactiveCrumb : {
    color         : dlsColor('grey3'),
    textTransform : toggleThemeStyle('none', 'capitalize'),
    '&:hover'     : {
      cursor : 'pointer'
    }
  },
  formSection : {
    left           : '50%',
    color          : dlsColor('grey3'),
    margin         : 0,
    display        : 'flex',
    fontSize       : '20px',
    position       : 'absolute',
    transform      : 'translateX(-50%)',
    alignItems     : 'center',
    // the current theme's alias for its Archivo Narrow font files ...
    fontFamily     : 'Roboto',
    fontWeight     : 400,
    lineHeight     : '24px',
    whiteSpace     : 'nowrap',
    textTransform  : 'capitalize',
    justifyContent : 'center'
  },
  dialogTitle : {
    display    : 'flex',
    position   : 'relative',
    alignItems : 'center'
  },
  breadcrumbContainer : {
    display    : 'flex',
    alignItems : 'center'
  }
});

export interface AssetDetailViewProps extends
  AssetAssociation,
  PageViewSubGroupsProps {
  onCloseDialog?: Function;
  onBreadcrumbBackClick?: Function;
  isEditMode?: boolean;
  accountId?: any;
  setAssetDetailedRow?: Function;
  assetDetailedSelectedRow?: any;
  includeAssetGridCheckboxes?: boolean;
  getAssetDetailedDoneBtnClick?: Function;
  singleRowSelection?: boolean;
  isCtcAdmin?: boolean;
  assetTypeValue?: AssetsFilters['assetType'];
  searchPlaceholder?: string;
  filterOn?: GenericObject;
  externalColumnDefinitions?: Function;
  isLiveSelection?: boolean;
  crumbLabels?: string[];
  hideSubGroups?: boolean;
  getAssetSubGroupSwitchStatus?: (hasAssetSubGroupSwitch: boolean) => void;
};

/**
 * The DeviceDetailView will be used to show the grid in the dialog
 * including search option.
 *
 * @param onCloseDialog Callback function fired when the dialog requests to be closed
 * @param onBreadcrumbBackClick Takes back to the place from where this dialog is was opened
 * @param isEditMode Checks the breadcrumb label whether opened from add/edit
 * @param accountId Gets the selected group chooser account id
 * @param setAssetDetailedRow Function used to set the selected row data
 * @param getAssetDetailedDoneBtnClick Function used to check whether Submit button is clicked
 * @param assetDetailedSelectedRow Uses to get the selected row data
 */
export const AssetDetailView = ({
  onCloseDialog,
  onBreadcrumbBackClick,
  isEditMode,
  accountId,
  setAssetDetailedRow,
  getAssetDetailedDoneBtnClick,
  externalColumnDefinitions,
  assetDetailedSelectedRow,
  searchPlaceholder,
  assetTypeValue,
  assetId,
  assetName,
  Features,
  getAssetSubGroupSwitchStatus,
  crumbLabels = [],
  filterOn = {},
  isLiveSelection = false,
  singleRowSelection = false,
  isCtcAdmin = false,
  includeAssetGridCheckboxes = false,
  includeFilteredAssetAssociation = false,
  enableSubgroupToggle = false,
  hideSubGroups = false
}: AssetDetailViewProps) => {
  const {
    isVisible,
    onRemove,
    timeout
  } = useAnimatedMount();

  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();

  const editorStyles      = useExtendStyles((theme) => styles(theme, isCurrentTheme), styleOverrides);
  const classes           = editorStyles();
  const isDoneBtnDisabled = assetDetailedSelectedRow?.length === 0;

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setAssetViewSubGroups,
    view       : !Features || enableSubgroupToggle,
    preference : PREFERENCE
  });

  useEffect(() => () => {
    getAssetDetailedDoneBtnClick?.(false);
    getAssetSubGroupSwitchStatus?.(viewSubGroups);
  }, [viewSubGroups]);

  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };

  const crumb1 = {
    label   : crumbLabels?.[1] ?? translate(`admin-assets:form.sections.${isEditMode ? 'edit' : 'add'}Asset`),
    click   : true,
    onClick : function(e) {
      onBreadcrumbBackClick?.(e);
    }
  };

  const crumbData = defineBreadcrumbs({
    crumbs : [
      crumb1,
      {
        label : crumbLabels?.[0] ?? translate('common:editors.asset.placeholder'),
        click : false
      }
    ],
    classes : crumbClasses
  });

  function handleSelect() {
    onClose(false);
    getAssetDetailedDoneBtnClick?.(true);
    getAssetSubGroupSwitchStatus?.(viewSubGroups);
  }

  function onClose(fromCloseBtn = true) {
    onRemove(onCloseDialog, fromCloseBtn);
    getAssetDetailedDoneBtnClick?.(false);
    getAssetSubGroupSwitchStatus?.(viewSubGroups);
  }

  const toggleSubGroupsView = useCallback(() => {
    toggleViewSubGroups?.(!viewSubGroups);
  }, [viewSubGroups]);

  return (
    <Dialog disableEnforceFocus open={open}
      transitionDuration={timeout}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={isCurrentTheme ? classes.dialogTitle : ''}>
          <div className={isCurrentTheme ? classes.breadcrumbContainer : ''}>
            <Breadcrumb icon={<ChevronLeft className={classes.chevron} />}
              height={'30px'}
              style={isCurrentTheme ? {alignItems: 'center'} : undefined}
              iconClick={onBreadcrumbBackClick}>
              {crumbData}
            </Breadcrumb>
          </div>
          <Render.If condition={isCurrentTheme}>
            <Typography component='h1' variant='h2' className={classes.formSection}>
              {translate('common:entityLabels.assets')}
            </Typography>
          </Render.If>
        </div>
      </DialogTitle>
      <div className={classes.form}>
        <div className={classes.formWrapper}>
          <Render.If condition={isLegacyTheme}>
            <div className={classes.deviceDetailHeader}>
              <div className={classes.label}>{translate('common:entityLabels.assets')}</div>
              {hideSubGroups && (
                <div className={classes.subAccount}>
                  <ViewSubGroupsSwitch checked={viewSubGroups}
                    onChange={toggleSubGroupsView}
                    preference={PREFERENCE}
                    position={ViewSubGroupsPosition.GRID_CTAS}
                    labelColor={dlsColor('grey2')} />
                </div>
              )}
            </div>
          </Render.If>
          <DialogContent className={classes.paddingNone}>
            <AssetDetailViewGrid singleRowSelection={singleRowSelection}
              includeFilteredAssetAssociation={includeFilteredAssetAssociation}
              externalColumnDefinitions={externalColumnDefinitions}
              assetDetailedSelectedRow={assetDetailedSelectedRow}
              includeCheckboxes={includeAssetGridCheckboxes}
              setAssetDetailedRow={setAssetDetailedRow}
              searchPlaceholder={searchPlaceholder}
              isLiveSelection={isLiveSelection}
              assetTypeValue={assetTypeValue}
              isEditMode={isEditMode}
              assetId={assetId}
              assetName={assetName}
              accountId={accountId}
              filterOn={filterOn}
              isHideFilter={isCtcAdmin}
              hideSubGroups={hideSubGroups}
              viewSubGroups={viewSubGroups}
              toggleViewSubGroups={toggleViewSubGroups} />
          </DialogContent>
        </div>
      </div>
      <DialogActions className={classes.formActions}>
        <TertiaryButton onClick={function(e) {
          onClose();
        }}>
          {translate('common:editors.shared.cancel')}
        </TertiaryButton>
        <div className={classes.primaryButton}>
          <PrimaryButton fullWidth disabled={isCtcAdmin ? false : isDoneBtnDisabled} onClick={handleSelect}>
            {translate('common:editors.shared.done')}
          </PrimaryButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AssetDetailView;
